"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCheckCircle,
  faCircleExclamation,
  faClipboard,
  faClock,
  faCreditCard,
  faQrcode,
  faSpinner,
  faXmark
} from "@fortawesome/free-solid-svg-icons";
import { useUser } from "@/context/UserContext";
import { showToast } from "@/components/ui/Toast";
import { CardPaymentModal } from "./CardPaymentModal";
import type { BillingEvent, PaymentStatus, CardPaymentResponse } from "@/lib/payments";
import {
  createPixPayment,
  fetchPaymentEventStatus,
  type CreatePixPaymentPayload
} from "@/lib/payments";

type PaymentMethod = "pix" | "card";

type PlanTarget = {
  kind: "plan";
  planCode: string;
  planName: string;
  amount: number;
  currency?: string;
  benefits?: string[];
};

type OneTimeTarget = {
  kind: "one_time";
  productId: string;
  productTitle: string;
  amount: number;
  currency?: string;
};

type PaymentTarget = PlanTarget | OneTimeTarget;

export interface UnifiedCheckoutModalProps {
  isOpen: boolean;
  target: PaymentTarget | null;
  onClose: () => void;
  onSuccess?: (event: BillingEvent) => void;
  onStatusChange?: (status: PaymentStatus) => void;
  pixOnly?: boolean;
  cardOnly?: boolean;
}

interface PixState {
  paymentId: string;
  expiresAt: string;
  qrCodeBase64: string | null;
  qrCodeText: string | null;
  ticketUrl: string | null;
  amount: number;
  currency: string;
}

const formatCurrency = (value: number, currency: string) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency
  }).format(value);
};

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.max(0, seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${secs}`;
};

const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid: "Pagamento confirmado",
  pending: "Aguardando pagamento",
  failed: "Pagamento recusado",
  canceled: "Pagamento cancelado",
  refunded: "Pagamento reembolsado",
  expired: "Pagamento expirado"
};

export function UnifiedCheckoutModal({ isOpen, target, onClose, onSuccess, onStatusChange, pixOnly = false, cardOnly = false }: UnifiedCheckoutModalProps) {
  const { user } = useUser();
  const router = useRouter();
  const [method, setMethod] = useState<PaymentMethod | null>(null);
  const [pixState, setPixState] = useState<PixState | null>(null);
  const [pixStatus, setPixStatus] = useState<PaymentStatus | null>(null);
  const [pixEvent, setPixEvent] = useState<BillingEvent | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const pixAutoInitRef = useRef(false);

  const isAuthenticated = Boolean(user);

  const amountLabel = useMemo(() => {
    if (!target) return "";
    const currency = target.currency ?? "BRL";
    return formatCurrency(target.amount, currency);
  }, [target]);

  useEffect(() => {
    if (!isOpen) {
      setMethod(null);
      setPixState(null);
      setPixStatus(null);
      setPixEvent(null);
      setCountdown(0);
      setErrorMessage(null);
      setShowCardModal(false);
      pixAutoInitRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const startCountdown = (expiresAt: string) => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    const expiration = new Date(expiresAt).getTime();
    const updateCountdown = () => {
      const diff = Math.max(0, Math.floor((expiration - Date.now()) / 1000));
      setCountdown(diff);
      if (diff === 0) {
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
          countdownRef.current = null;
        }
        setPixStatus((current) => (current === "pending" ? "expired" : current));
      }
    };
    updateCountdown();
    countdownRef.current = setInterval(updateCountdown, 1_000);
  };

  const stopPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const handleStatusUpdate = (status: PaymentStatus, event?: BillingEvent) => {
    setPixStatus(status);
    if (event) {
      setPixEvent(event);
    }
    onStatusChange?.(status);

    if (status === "paid" && event) {
      showToast("Pagamento confirmado com sucesso!", "success");
      onSuccess?.(event);
    }

    if (["failed", "canceled", "expired"].includes(status)) {
      const message = STATUS_LABEL[status];
      showToast(message, "error");
    }

    if (status !== "pending") {
      stopPolling();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
  };

  const pollPaymentStatus = (paymentId: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    const poll = async () => {
      try {
        const response = await fetchPaymentEventStatus(paymentId);
        if (!response.event) {
          return;
        }
        setPixEvent(response.event);
        handleStatusUpdate(response.event.status, response.event);
      } catch (error) {
        console.error("Erro ao verificar status do pagamento", error);
      }
    };

    pollingRef.current = setInterval(poll, 5_000);
    void poll();
  };

  const preparePixPayment = async (payload: CreatePixPaymentPayload) => {
    stopPolling();
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setPixState(null);
    setPixStatus(null);
    setPixEvent(null);
    setCountdown(0);
    setIsProcessing(true);
    setErrorMessage(null);
    try {
      const response = await createPixPayment(payload);
      setPixState({
        paymentId: response.paymentId,
        expiresAt: response.expiresAt,
        qrCodeBase64: response.qrCodeBase64,
        qrCodeText: response.qrCodeText,
        ticketUrl: response.ticketUrl,
        amount: response.amount,
        currency: response.currency
      });
      setPixStatus(response.status);
      startCountdown(response.expiresAt);
      pollPaymentStatus(response.paymentId);
    } catch (error) {
      console.error("Erro ao gerar pagamento Pix", error);
      setErrorMessage(error instanceof Error ? error.message : "Não foi possível gerar o QR Code Pix");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleMethodSelection = (selected: PaymentMethod) => {
    if (pixOnly && selected !== "pix") {
      return;
    }
    if (!isAuthenticated) {
      showToast("Faça login para continuar", "info");
      onClose();
      router.push("/login");
      return;
    }

    if (selected !== "pix") {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      setPixState(null);
      setPixStatus(null);
      setPixEvent(null);
      setCountdown(0);
    }

    setMethod(selected);
    setErrorMessage(null);
    if (selected === "pix" && target) {
      const payload: CreatePixPaymentPayload = {
        intentType: target.kind === "plan" ? "subscription" : "one_time",
        planCode: target.kind === "plan" ? target.planCode : undefined,
        productId: target.kind === "one_time" ? target.productId : undefined,
        productTitle: target.kind === "one_time" ? target.productTitle : undefined,
        // Pass artwork id when present so server can link one-time purchase to specific artwork
        artworkId: (target.kind === "one_time" ? (target as any).artworkId : undefined) ?? undefined
      };
      void preparePixPayment(payload);
    }
  };

  const handleCardCheckout = async () => {
    if (!target) return;
    if (!isAuthenticated) {
      showToast("Faça login para continuar", "info");
      onClose();
      router.push("/login");
      return;
    }

    // Abre o modal de cartão do MercadoPago
    setShowCardModal(true);
  };

  const handleCardPaymentSuccess = (response: CardPaymentResponse) => {
    showToast("Pagamento aprovado com sucesso!", "success");
    setShowCardModal(false);
    onSuccess?.();
    onClose();
  };

  const handleCardModalClose = () => {
    setShowCardModal(false);
  };

  const handleCopyCode = async () => {
    if (!pixState?.qrCodeText) return;
    try {
      await navigator.clipboard.writeText(pixState.qrCodeText);
      showToast("Código copiado com sucesso", "success");
    } catch (error) {
      console.error("Erro ao copiar código Pix", error);
      showToast("Não foi possível copiar o código", "error");
    }
  };

  useEffect(() => {
    if (!isOpen || !pixOnly || !target || pixAutoInitRef.current) {
      return;
    }
    if (!isAuthenticated) {
      showToast("Faça login para continuar", "info");
      onClose();
      router.push("/login");
      return;
    }
    pixAutoInitRef.current = true;
    setMethod("pix");
    const payload: CreatePixPaymentPayload = {
      intentType: target.kind === "plan" ? "subscription" : "one_time",
      planCode: target.kind === "plan" ? target.planCode : undefined,
      productId: target.kind === "one_time" ? target.productId : undefined,
      productTitle: target.kind === "one_time" ? target.productTitle : undefined
    };
    void preparePixPayment(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, pixOnly, target, isAuthenticated]);

  // Auto-init card payment when cardOnly=true
  useEffect(() => {
    if (!isOpen || !cardOnly || !target) return;
    if (!isAuthenticated) {
      showToast("Faça login para continuar", "info");
      onClose();
      router.push("/login");
      return;
    }
    // Directly open CardPaymentModal, skip method selection
    setShowCardModal(true);
  }, [isOpen, cardOnly, target, isAuthenticated]);

  if (!isOpen || !target) {
    return null;
  }

  // When cardOnly, render only the CardPaymentModal without the wrapper
  if (cardOnly) {
    return (
      <CardPaymentModal
        isOpen={showCardModal}
        onClose={onClose}
        onSuccess={(response) => {
          showToast("Pagamento aprovado com sucesso!", "success");
          setShowCardModal(false);
          onSuccess?.();
          onClose();
        }}
        onBack={onClose}
        target={{
          kind: target.kind,
          planCode: target.kind === "plan" ? target.planCode : undefined,
          productId: target.kind === "one_time" ? target.productId : undefined,
          productTitle: target.kind === "one_time" ? target.productTitle : undefined,
          planName: target.kind === "plan" ? target.planName : (target.productTitle ?? "Compra Avulsa"),
          amount: target.amount,
          currency: target.currency
        }}
      />
    );
  }

  const content = (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/80 px-4 py-6">
      <div className="relative w-full max-w-2xl rounded-3xl border border-white/10 bg-[#0f011b] p-6 shadow-[0_25px_80px_rgba(124,58,237,0.35)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white/70 transition hover:bg-white/20"
          aria-label="Fechar"
        >
          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
        </button>

        <div className="space-y-6 text-white">
          <div className="inline-flex items-center gap-2 rounded-full border border-brand-secondary/40 bg-brand-secondary/10 px-3 py-1 text-xs uppercase tracking-[0.25em] text-brand-secondary">
            Pagamento seguro
          </div>

          <header className="space-y-2">
            <h3 className="text-2xl font-semibold">
              {target.kind === "plan" ? `Assinar ${target.planName}` : `Comprar ${target.productTitle}`}
            </h3>
            <p className="text-sm text-white/60">
              Valor: <span className="font-semibold text-white">{amountLabel}</span>
            </p>
          </header>

          <div className={clsx("grid gap-3", pixOnly ? "sm:grid-cols-1" : "sm:grid-cols-2")}>
            <button
              type="button"
              onClick={() => handleMethodSelection("pix")}
              className={clsx(
                "flex flex-col items-start gap-2 rounded-2xl border px-4 py-4 text-left transition",
                method === "pix" ? "border-brand-secondary/80 bg-brand-secondary/20 text-white" : "border-white/15 bg-white/5 text-white/70 hover:border-brand-secondary/50",
                pixOnly && "cursor-default"
              )}
            >
              <span className="flex items-center gap-2 text-base font-semibold text-white">
                <FontAwesomeIcon icon={faQrcode} className="h-4 w-4" />
                Pix
              </span>
              <p className="text-xs text-white/60">Gere o QR Code Pix com validade de 30 minutos e liberação automática.</p>
            </button>

            {!pixOnly && (
              <button
                type="button"
                onClick={handleCardCheckout}
                className={clsx(
                  "flex flex-col items-start gap-2 rounded-2xl border px-4 py-4 text-left transition",
                  "border-white/15 bg-white/5 text-white/70 hover:border-brand-secondary/50"
                )}
              >
                <span className="flex items-center gap-2 text-base font-semibold text-white">
                  <FontAwesomeIcon icon={faCreditCard} className="h-4 w-4" />
                  Cartão de crédito
                </span>
                <p className="text-xs text-white/60">Pagamento recorrente (planos) ou único para compras avulsas via MercadoPago.</p>
              </button>
            )}
          </div>

          {errorMessage && (
            <div className="rounded-2xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-sm text-red-200">
              <FontAwesomeIcon icon={faCircleExclamation} className="mr-2 h-4 w-4" />
              {errorMessage}
            </div>
          )}

          {method === "pix" && (
            <div className="space-y-4 rounded-2xl border border-brand-secondary/40 bg-brand-secondary/10 p-5">
              {isProcessing && !pixState ? (
                <div className="flex flex-col items-center gap-3 text-sm text-white/70">
                  <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 animate-spin" />
                  <p>Gerando QR Code Pix...</p>
                </div>
              ) : pixState ? (
                <div className="grid gap-4 sm:grid-cols-[200px_1fr]">
                  <div className="flex flex-col items-center gap-3">
                    {pixState.qrCodeBase64 ? (
                      <img
                        src={pixState.qrCodeBase64}
                        alt="QR Code Pix"
                        className="h-44 w-44 rounded-xl border border-white/10 bg-white"
                      />
                    ) : (
                      <div className="flex h-44 w-44 items-center justify-center rounded-xl border border-dashed border-white/20 text-xs text-white/60">
                        QR Code indisponível
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={handleCopyCode}
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white/20"
                    >
                      <FontAwesomeIcon icon={faClipboard} className="h-3.5 w-3.5" />
                      Copiar código
                    </button>
                  </div>
                  <div className="space-y-4 text-sm">
                    <div className="flex items-center gap-2 text-white/70">
                      <FontAwesomeIcon icon={faClock} className="h-4 w-4 text-brand-secondary" />
                      <span>Expira em {formatCountdown(countdown)}</span>
                    </div>
                    <div className="rounded-xl border border-white/15 bg-black/30 p-4 text-xs text-white/70">
                      <p className="font-semibold text-white">Código copia e cola</p>
                      <p className="mt-2 break-all font-mono text-[11px] text-white/80">
                        {pixState.qrCodeText ?? "-"}
                      </p>
                    </div>
                    {pixState.ticketUrl && (
                      <a
                        href={pixState.ticketUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-xs font-semibold text-brand-secondary transition hover:text-brand-secondary/80"
                      >
                        Visualizar no app do banco
                        <FontAwesomeIcon icon={faArrowRight} className="h-3 w-3" />
                      </a>
                    )}
                    {pixStatus && (
                      <div className={clsx(
                        "inline-flex items-center gap-2 rounded-full px-4 py-1 text-xs font-semibold",
                        pixStatus === "paid" && "bg-emerald-500/20 text-emerald-200 border border-emerald-400/40",
                        pixStatus === "pending" && "bg-amber-500/20 text-amber-100 border border-amber-400/40",
                        pixStatus === "expired" && "bg-red-500/20 text-red-200 border border-red-400/40",
                        ["failed", "canceled"].includes(pixStatus) && "bg-red-500/20 text-red-200 border border-red-400/40"
                      )}>
                        <FontAwesomeIcon
                          icon={pixStatus === "paid" ? faCheckCircle : pixStatus === "pending" ? faClock : faCircleExclamation}
                          className="h-3.5 w-3.5"
                        />
                        {STATUS_LABEL[pixStatus]}
                      </div>
                    )}
                    <p className="text-xs text-white/60">
                      O status é atualizado automaticamente assim que o pagamento for confirmado pelo Mercado Pago.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-white/60">
                  Clique em Pix novamente para gerar um novo QR Code.
                </div>
              )}
            </div>
          )}



        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(content, document.body)}
      {showCardModal && target && (
        <CardPaymentModal
          isOpen={showCardModal}
          onClose={handleCardModalClose}
          onSuccess={handleCardPaymentSuccess}
          onBack={handleCardModalClose}
          target={{
            kind: target.kind,
            planCode: target.kind === "plan" ? target.planCode : undefined,
            productId: target.kind === "one_time" ? target.productId : undefined,
            productTitle: target.kind === "one_time" ? target.productTitle : undefined,
            planName: target.kind === "plan" ? target.planName : (target.productTitle ?? "Compra Avulsa"),
            amount: target.amount,
            currency: target.currency
          }}
        />
      )}
    </>
  );
}
